import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  FormControlLabel,
  Checkbox,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import PriceDisplay from "components/PriceDisplay";
import iaasService from "services/iaasService";
import Swal from "sweetalert2";
import { AuthContext } from "context";

function NetworkRouterConfig({ onBack }) {
  const { user } = useContext(AuthContext);
  const [bandwidth, setBandwidth] = useState("100mbit");
  const [vpnTunnels, setVpnTunnels] = useState(1);
  const [managedFirewall, setManagedFirewall] = useState(false);
  const [haEnabled, setHaEnabled] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(false);

  const showSuccessNotification = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Erfolg!',
      text: message,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK'
    });
  };

  const showErrorNotification = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Fehler',
      text: message,
      confirmButtonColor: '#d33',
      confirmButtonText: 'OK'
    });
  };

  // Berechne den Preis mit Berücksichtigung des Partner-Status
  useEffect(() => {
    const config = {
      bandwidth,
      vpnTunnels,
      managedFirewall,
      haEnabled
    };
    
    // Berechne den Basispreis
    const basePrice = iaasService.calculateNetworkRouterPrice(config);
    
    const isPartner = user?.isPartner === true;

    if (isPartner) {
      // 20% Rabatt für Partner
      setOriginalPrice(basePrice);
      setTotalPrice(basePrice * 0.8);
      setDiscountApplied(true);
    } else {
      setTotalPrice(basePrice);
      setOriginalPrice(null);
      setDiscountApplied(false);
    }
  }, [bandwidth, vpnTunnels, managedFirewall, haEnabled, user?.isPartner]);

  const handleOrder = async () => {
    if (!agreedToTerms) {
      showErrorNotification("Bitte stimmen Sie den AGBs zu.");
      return;
    }

    try {
      const configuration = {
        bandwidth,
        vpnTunnels,
        managedFirewall,
        haEnabled
      };
      
      // Füge Rabattinformationen hinzu, wenn ein Rabatt angewendet wurde
      if (discountApplied) {
        configuration.priceInfo = {
          originalPrice: originalPrice,
          discountApplied: true
        };
      }

      const response = await iaasService.orderService(
        'private-network',
        configuration
      );

      if (response) {
        showSuccessNotification("Ihre Bestellung wurde erfolgreich aufgegeben!");
        setTimeout(() => {
          window.location.href = "/iaas/server";
        }, 2000);
      }
    } catch (error) {
      console.error("Fehler bei der Bestellung:", error);
      showErrorNotification("Es gab ein Problem bei der Bestellung. Bitte versuchen Sie es später erneut.");
    }
  };

  return (
    <SoftBox>
      <SoftTypography variant="h4" mb={2}>
        UDS Private Network Router Konfiguration
      </SoftTypography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Bandbreite</InputLabel>
            <Select
              value={bandwidth}
              onChange={(e) => setBandwidth(e.target.value)}
              label="Bandbreite"
            >
              <MenuItem value="100mbit">100 Mbit/s (10€/Monat)</MenuItem>
              <MenuItem value="1gbit">1 Gbit/s (25€/Monat)</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Anzahl VPN-Tunnel"
            value={vpnTunnels}
            onChange={(e) => setVpnTunnels(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
            helperText="1-100 Tunnel möglich (2€ pro Tunnel/Monat)"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={managedFirewall}
                onChange={(e) => setManagedFirewall(e.target.checked)}
              />
            }
            label="Site2Site Firewall (+20€/Monat)"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={haEnabled}
                onChange={(e) => setHaEnabled(e.target.checked)}
              />
            }
            label="High Availability (HA) Setup"
          />
        </Grid>

        <Grid item xs={12}>
          <SoftTypography variant="h5" fontWeight="bold">
            Monatlicher Preis: {totalPrice.toFixed(2)} €
          </SoftTypography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
            }
            label={
              <SoftTypography variant="body2">
                Ich akzeptiere die{" "}
                <a href="https://uds-solutions.de/agb" target="_blank" rel="noopener noreferrer">
                  AGBs
                </a>{" "}
                und bestelle den Router kostenpflichtig.
              </SoftTypography>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <SoftBox display="flex" justifyContent="space-between">
            <Button variant="contained" color="light" onClick={onBack}>
              Zurück
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOrder}
              disabled={!agreedToTerms}
            >
              Jetzt bestellen
            </Button>
          </SoftBox>
        </Grid>
      </Grid>
    </SoftBox>
  );
}

NetworkRouterConfig.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default NetworkRouterConfig;